import { useState } from 'react';
import { Search, Check, AlertCircle } from 'lucide-react';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';

// Groups come from GET /api/groups, which (pre-bootstrap) lists the tenant's
// securityEnabled groups via Graph with full pagination. Large tenants can
// have thousands — filter client-side but cap what we render.

interface AadGroup { aadGroupId: string; displayName: string; description?: string; }

const MAX_SHOWN = 100;

export function BootstrapWizard({ onComplete }: { onComplete: () => void }) {
  const [selected, setSelected] = useState<AadGroup | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const { data: groups, loading } = useApi(() => api.listGroups(), { deps: [] });

  const filtered = (groups || []).filter(g =>
    !search || g.displayName.toLowerCase().includes(search.toLowerCase())
  );
  const shown = filtered.slice(0, MAX_SHOWN);

  async function submit() {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.bootstrap(selected.aadGroupId);
      onComplete();
    } catch (e: any) {
      setError(e.message || 'Setup failed');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-lg border border-gray-200 p-6">
        <h1 className="text-xl font-semibold text-gray-900 mb-1">Welcome to Quick Dial</h1>
        <p className="text-sm text-gray-600 mb-5">
          To get started, choose the Microsoft Entra security group whose members will be Quick Dial administrators. This usually means your IT or helpdesk team.
        </p>

        <div className="mb-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search security groups..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
          />
        </div>

        <div className="border border-gray-200 rounded max-h-72 overflow-y-auto mb-4">
          {loading ? (
            <div className="p-6 text-center text-sm text-gray-400">Loading groups…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">No matches.</div>
          ) : (
            <>
              {shown.map(g => (
                <button
                  key={g.aadGroupId}
                  onClick={() => setSelected(g)}
                  className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 flex items-center justify-between ${selected?.aadGroupId === g.aadGroupId ? 'bg-purple-50' : ''}`}
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">{g.displayName}</div>
                    {g.description && <div className="text-xs text-gray-500">{g.description}</div>}
                  </div>
                  {selected?.aadGroupId === g.aadGroupId && <Check className="w-4 h-4 text-purple-600" />}
                </button>
              ))}
              {filtered.length > MAX_SHOWN && (
                <div className="p-2.5 text-center text-xs text-gray-400 bg-gray-50">
                  Showing {MAX_SHOWN} of {filtered.length} matches — refine your search to narrow down
                </div>
              )}
            </>
          )}
        </div>

        {error && (
          <div className="mb-3 px-3 py-2 bg-red-50 border border-red-100 rounded text-xs text-red-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            You must be a Global Administrator to complete this step.
          </p>
          <button
            onClick={submit}
            disabled={!selected || submitting}
            className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded font-medium"
          >
            {submitting ? 'Setting up…' : 'Complete setup'}
          </button>
        </div>
      </div>
    </div>
  );
}
